import { isApiTruthy } from './apiValues';

/**
 * Customer-facing copy when add-to-cart / ordering is blocked.
 *
 * @param {object|null|undefined} restaurant
 * @param {boolean} hasAddress
 * @param {'pickup'|'delivery'} [orderMode='delivery']
 * @returns {{ reason: string, title: string, message: string }|null}
 */
export function resolveOrderBlock({ restaurant, hasAddress, orderMode = 'delivery' }) {
  if (!restaurant) return null;

  const isPickup = orderMode === 'pickup';

  if (!isPickup && !hasAddress) {
    return {
      reason: 'no_address',
      title: 'Delivery address required',
      message:
        'Set your delivery address using the location bar at the top of the page before adding items to your cart.',
    };
  }

  if (isApiTruthy(restaurant.can_order)) {
    return null;
  }
  const reason = String(restaurant.can_order_reason || '').trim() || 'unavailable';
  const apiMessage = String(restaurant.operating_message || '').trim();

  switch (reason) {
    case 'outside_delivery':
      return {
        reason,
        title: 'Outside delivery area',
        message:
          apiMessage ||
          'This restaurant does not deliver to your address. You can browse the menu, but ordering is not available.',
      };
    case 'pickup_disabled':
      return {
        reason,
        title: 'Pickup unavailable',
        message: apiMessage || 'Pickup is not available at this location right now.',
      };
    case 'delivery_disabled':
      return {
        reason,
        title: 'Delivery unavailable',
        message: apiMessage || 'Delivery is not available at this location right now.',
      };
    case 'holiday':
      return {
        reason,
        title: 'Closed for holiday',
        message: apiMessage || 'This location is closed for a holiday today.',
      };
    case 'no_address':
      return {
        reason,
        title: 'Delivery address required',
        message:
          'Set your delivery address using the location bar at the top of the page before adding items to your cart.',
      };
    case 'closed':
    case 'closed_operational':
      return {
        reason,
        title: 'Restaurant is closed',
        message: apiMessage || 'This location is closed right now. Please check back during opening hours.',
      };
    case 'orders_disabled':
      return {
        reason,
        title: 'Ordering unavailable',
        message: apiMessage || 'Online ordering is turned off for this location.',
      };
    case 'branch_unavailable':
      return {
        reason,
        title: 'Unavailable',
        message: apiMessage || 'This restaurant is not available for orders right now.',
      };
    default:
      return {
        reason,
        title: 'Cannot add to cart',
        message: apiMessage || 'Ordering is not available at this time.',
      };
  }
}

/** Badge CSS modifier from API `operating_status`. */
export function operatingStatusClass(status) {
  const s = String(status || 'open').toLowerCase();
  if (s === 'open') return 'open';
  if (s === 'holiday') return 'holiday';
  return 'closed';
}
